import type { AppDatabase } from './store';
import type { Id } from '../models';
import {
  buildPublicEventSnapshot,
  buildPublicGameSnapshot,
  type PublicEventScheduleRow,
  type PublicGameSnapshot,
} from './liveGameService';

export interface LeagueInsightMetric {
  label: string;
  value: string | number;
  detail: string;
}

export interface LeagueInsightPlayer {
  playerId: Id;
  playerName: string;
  teamId?: Id;
  teamName?: string;
  eventId: Id;
  eventName: string;
  eventSlug: string;
  goals: number;
  assists: number;
  points: number;
  penaltyMinutes: number;
}

export interface LeagueInsightTeam {
  teamId: Id;
  teamName: string;
  eventId: Id;
  eventName: string;
  eventSlug: string;
  primaryColor: string;
  points: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifferential: number;
  penaltyMinutes: number;
  trendLabel: string;
}

export interface LeagueInsightGame {
  gameId: Id;
  eventId: Id;
  eventName: string;
  eventSlug: string;
  startsAt?: string;
  divisionName?: string;
  homeTeamName: string;
  awayTeamName: string;
  homeScore: number;
  awayScore: number;
  decidedBy?: string;
  label: string;
}

export interface LeagueInsightGoalie {
  playerId: Id;
  playerName: string;
  teamId?: Id;
  teamName?: string;
  eventId: Id;
  eventName: string;
  eventSlug: string;
  gamesPlayed: number;
  savePct: number;
  gaa: number;
  wins: number;
  shutouts: number;
}

export interface LeagueInsightDivision {
  eventId: Id;
  eventName: string;
  eventSlug: string;
  divisionId?: Id;
  divisionName: string;
  teams: number;
  pointsSpread: number;
  averageGoalDifferential: number;
  oneGoalGames: number;
  label: string;
}

export interface LeagueScoringTrendPoint {
  key: string;
  label: string;
  games: number;
  goals: number;
  penaltyMinutes: number;
}

export interface LeagueInsightsSummary {
  metrics: LeagueInsightMetric[];
  hotPlayers: LeagueInsightPlayer[];
  hotTeams: LeagueInsightTeam[];
  closeGames: LeagueInsightGame[];
  comebackGames: LeagueInsightGame[];
  penaltyTeams: LeagueInsightTeam[];
  goalieLeaders: LeagueInsightGoalie[];
  divisionCompetitiveness: LeagueInsightDivision[];
  recentFinals: LeagueInsightGame[];
  scoringTrend: LeagueScoringTrendPoint[];
}

interface EventContext {
  eventId: Id;
  eventName: string;
  eventSlug: string;
}

export function buildLeagueInsights(db: AppDatabase): LeagueInsightsSummary {
  const activeEvents = db.tournaments
    .filter((event) => event.status !== 'archived')
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  const snapshots = activeEvents
    .map((event) => buildPublicEventSnapshot(db, event.id))
    .filter(isDefined);
  const gameSnapshots = db.games
    .map((game) => buildPublicGameSnapshot(db, game.id))
    .filter(isDefined);
  const finalGameSnapshots = gameSnapshots.filter((snapshot) => snapshot.game.status === 'final' || snapshot.game.status === 'forfeit');

  const hotPlayers = snapshots.flatMap((snapshot) =>
    snapshot.playerLeaders.points.slice(0, 8).map((row) => ({
      playerId: row.playerId,
      playerName: row.playerName,
      teamId: row.teamId,
      teamName: row.teamName,
      eventId: snapshot.tournament.id,
      eventName: snapshot.tournament.name,
      eventSlug: snapshot.tournament.slug,
      goals: row.goals,
      assists: row.assists,
      points: row.points,
      penaltyMinutes: row.penaltyMinutes,
    })),
  ).sort((a, b) => b.points - a.points || b.goals - a.goals).slice(0, 10);

  const hotTeams = snapshots.flatMap((snapshot) =>
    snapshot.analytics.teamPulse.map((team) => ({
      teamId: team.teamId,
      teamName: team.teamName,
      eventId: snapshot.tournament.id,
      eventName: snapshot.tournament.name,
      eventSlug: snapshot.tournament.slug,
      primaryColor: team.primaryColor,
      points: team.points,
      goalsFor: team.goalsFor,
      goalsAgainst: team.goalsAgainst,
      goalDifferential: team.goalDifferential,
      penaltyMinutes: team.penaltyMinutes,
      trendLabel: team.trendLabel,
    })),
  ).sort((a, b) => b.points - a.points || b.goalDifferential - a.goalDifferential).slice(0, 10);

  const penaltyTeams = [...hotTeams]
    .sort((a, b) => b.penaltyMinutes - a.penaltyMinutes || b.goalsFor - a.goalsFor)
    .slice(0, 8);

  const finalRows = finalGameSnapshots
    .map((snapshot) => toInsightGame(snapshot, { eventId: snapshot.tournament.id, eventName: snapshot.tournament.name, eventSlug: snapshot.tournament.slug }))
    .sort((a, b) => (b.startsAt ?? '').localeCompare(a.startsAt ?? ''));
  const closeGames = finalRows.filter((row) => Math.abs(row.homeScore - row.awayScore) <= 1).slice(0, 8);
  const comebackGames = finalGameSnapshots
    .filter(wasComebackWin)
    .map((snapshot) => toInsightGame(snapshot, { eventId: snapshot.tournament.id, eventName: snapshot.tournament.name, eventSlug: snapshot.tournament.slug }, 'Comeback win'))
    .slice(0, 8);

  const goalieLeaders = snapshots.flatMap((snapshot) =>
    snapshot.goalieLeaders
      .filter((row) => row.gamesPlayed > 0)
      .map((row) => ({
        playerId: row.playerId,
        playerName: row.playerName,
        teamId: row.teamId,
        teamName: row.teamName,
        eventId: snapshot.tournament.id,
        eventName: snapshot.tournament.name,
        eventSlug: snapshot.tournament.slug,
        gamesPlayed: row.gamesPlayed,
        savePct: row.savePct,
        gaa: row.gaa,
        wins: row.wins,
        shutouts: row.shutouts,
      })),
  ).sort((a, b) => b.savePct - a.savePct || a.gaa - b.gaa).slice(0, 8);

  const divisionCompetitiveness = snapshots.flatMap((snapshot) => buildDivisionCompetitiveness(snapshot.schedule, snapshot.tournament));
  const scoringTrend = buildScoringTrend(finalGameSnapshots);
  const totalGoals = finalGameSnapshots.reduce((sum, snapshot) => sum + snapshot.score.home + snapshot.score.away, 0);
  const totalPenaltyMinutes = finalGameSnapshots.reduce((sum, snapshot) => sum + snapshot.score.homePims + snapshot.score.awayPims, 0);
  const activePlayers = new Set(db.players.map((player) => player.id)).size;

  return {
    metrics: [
      { label: 'Events', value: activeEvents.length, detail: 'Active seasons and tournaments' },
      { label: 'Final games', value: finalGameSnapshots.length, detail: 'Official results captured' },
      { label: 'Goals', value: totalGoals, detail: 'Across final games' },
      { label: 'Players', value: activePlayers, detail: 'Rostered in Directory-backed events' },
      { label: 'Close games', value: closeGames.length, detail: 'Finals decided by one goal or less' },
      { label: 'Penalty minutes', value: totalPenaltyMinutes, detail: 'League-wide discipline signal' },
    ],
    hotPlayers,
    hotTeams,
    closeGames,
    comebackGames,
    penaltyTeams,
    goalieLeaders,
    divisionCompetitiveness,
    recentFinals: finalRows.slice(0, 10),
    scoringTrend,
  };
}

function toInsightGame(snapshot: PublicGameSnapshot, event: EventContext, label?: string): LeagueInsightGame {
  return {
    gameId: snapshot.game.id,
    eventId: event.eventId,
    eventName: event.eventName,
    eventSlug: event.eventSlug,
    startsAt: snapshot.startsAt,
    divisionName: snapshot.divisionName,
    homeTeamName: snapshot.homeTeam.name,
    awayTeamName: snapshot.awayTeam.name,
    homeScore: snapshot.result?.homeScore ?? snapshot.score.home,
    awayScore: snapshot.result?.awayScore ?? snapshot.score.away,
    decidedBy: snapshot.result?.decidedBy,
    label: label ?? formatDecision(snapshot.result?.decidedBy),
  };
}

function wasComebackWin(snapshot: PublicGameSnapshot): boolean {
  const winnerId = snapshot.result?.winningTeamId;
  if (!winnerId) return false;
  let homeScore = 0;
  let awayScore = 0;
  for (const event of snapshot.timeline.filter((row) => row.type === 'goal')) {
    if (event.teamId === snapshot.homeTeam.id) homeScore += 1;
    if (event.teamId === snapshot.awayTeam.id) awayScore += 1;
    const winnerScore = winnerId === snapshot.homeTeam.id ? homeScore : awayScore;
    const opponentScore = winnerId === snapshot.homeTeam.id ? awayScore : homeScore;
    if (winnerScore < opponentScore) return true;
  }
  return false;
}

function buildDivisionCompetitiveness(rows: PublicEventScheduleRow[], event: { id: Id; name: string; slug: string }): LeagueInsightDivision[] {
  const divisionGroups = new Map<string, PublicEventScheduleRow[]>();
  for (const row of rows) {
    if (!row.divisionId) continue;
    const list = divisionGroups.get(row.divisionId) ?? [];
    list.push(row);
    divisionGroups.set(row.divisionId, list);
  }
  return Array.from(divisionGroups.entries()).map(([divisionId, group]) => {
    const finalRows = group.filter((row) => row.result);
    const teamPoints = new Map<Id, number>();
    const goalDifferentials = new Map<Id, number>();
    for (const row of finalRows) {
      const homePoints = row.result!.homeScore > row.result!.awayScore ? 2 : row.result!.homeScore === row.result!.awayScore ? 1 : 0;
      const awayPoints = row.result!.awayScore > row.result!.homeScore ? 2 : row.result!.homeScore === row.result!.awayScore ? 1 : 0;
      teamPoints.set(row.homeTeam.id, (teamPoints.get(row.homeTeam.id) ?? 0) + homePoints);
      teamPoints.set(row.awayTeam.id, (teamPoints.get(row.awayTeam.id) ?? 0) + awayPoints);
      goalDifferentials.set(row.homeTeam.id, (goalDifferentials.get(row.homeTeam.id) ?? 0) + row.result!.homeScore - row.result!.awayScore);
      goalDifferentials.set(row.awayTeam.id, (goalDifferentials.get(row.awayTeam.id) ?? 0) + row.result!.awayScore - row.result!.homeScore);
    }
    const points = Array.from(teamPoints.values());
    const pointSpread = points.length > 0 ? Math.max(...points) - Math.min(...points) : 0;
    const averageGoalDifferential = average(Array.from(goalDifferentials.values()).map(Math.abs));
    const oneGoalGames = finalRows.filter((row) => Math.abs(row.result!.homeScore - row.result!.awayScore) <= 1).length;
    return {
      eventId: event.id,
      eventName: event.name,
      eventSlug: event.slug,
      divisionId,
      divisionName: group[0]?.divisionName ?? 'Division',
      teams: new Set(group.flatMap((row) => [row.homeTeam.id, row.awayTeam.id])).size,
      pointsSpread: pointSpread,
      averageGoalDifferential,
      oneGoalGames,
      label: pointSpread <= 4 ? 'Tight race' : pointSpread <= 10 ? 'Sorting out' : 'Clear separation',
    };
  }).sort((a, b) => a.pointsSpread - b.pointsSpread || b.oneGoalGames - a.oneGoalGames).slice(0, 8);
}

function buildScoringTrend(finals: PublicGameSnapshot[]): LeagueScoringTrendPoint[] {
  const groups = new Map<string, LeagueScoringTrendPoint>();
  for (const snapshot of finals) {
    const key = snapshot.startsAt ? new Date(snapshot.startsAt).toISOString().slice(0, 10) : 'unknown';
    const current = groups.get(key) ?? {
      key,
      label: snapshot.startsAt ? new Date(snapshot.startsAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : 'Date TBD',
      games: 0,
      goals: 0,
      penaltyMinutes: 0,
    };
    current.games += 1;
    current.goals += snapshot.score.home + snapshot.score.away;
    current.penaltyMinutes += snapshot.score.homePims + snapshot.score.awayPims;
    groups.set(key, current);
  }
  return Array.from(groups.values()).sort((a, b) => a.key.localeCompare(b.key)).slice(-8);
}

function formatDecision(decidedBy?: string): string {
  if (decidedBy === 'shootout') return 'SO final';
  if (decidedBy === 'overtime') return 'OT final';
  if (decidedBy === 'forfeit') return 'Forfeit';
  return 'Final';
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function isDefined<T>(value: T | undefined | null): value is T {
  return Boolean(value);
}
